import React from "react";
import {
  View,
  StyleSheet,
  Pressable,
  TouchableWithoutFeedback,
  TouchableOpacity,
} from "react-native";

// Custom Imports
import color from "@constants/color";
import { scale } from "@utils/scale";
import t from "@constants/localization";
import New from "@assets/svg/ClTasks/New";
import CustomText from "@components/CustomText";
import RightArrow from "@assets/svg/RightArrow";
import Coupon from "@assets/svg/ClTasks/Coupon";
import parseTemplate from "@utils/parseTemplate";
import CallIcon from "@assets/svg/ClTasks/CallIcon";
import CartIcon from "@assets/svg/ClTasks/CartIcon";
import { fontFamily } from "@utils/styles/fonts";
import { getDayMonthYear } from "@utils/getFormattedDate";
import WhatsappIcon from "@assets/svg/ClTasks/WhatsappIcon";
import { CONTEST_EVENTS } from "@constants/analyticsEventsCT";
import { shareOnWhatsapp, InvokeType } from "@utils/shareEntity";
import handleCallButtonPress from "@utils/handleCallButtonPress";

const PATH = "screens/Contest";

const OrderedCard = ({
  task,
  navigation,
  index,
  app_link = "",
  contest_id,
  onPressOrder,
  trackEvent = () => {},
}) => {
  const getTemplate = () => {
    const temp = parseTemplate(
      task?.wa_template,
      { customer_name: task?.user_name, app_link, " ": " ", "": " " },
      "$",
    );
    return temp;
  };

  const handleAnalytics = ({ type, sub_type }) => {
    switch (type) {
      case CONTEST_EVENTS.ORDER_CLICKED:
        trackEvent(CONTEST_EVENTS.ORDER_CLICKED, {
          screen: "Contest Orders",
          order_id: task.user_phone,
        });
        break;
      case CONTEST_EVENTS.CTA_CLICKED:
        trackEvent(CONTEST_EVENTS.CTA_CLICKED, {
          screen: "Contest task page",
          CTA_type: sub_type,
        });
        break;
      case CONTEST_EVENTS.CART_VIEWED:
        trackEvent(CONTEST_EVENTS.CART_VIEWED, {
          screen: "Contest task page",
          cart_value: task?.cartTotal,
          cart_items: task.cart.length,
          wallet_cashback: task?.wallet_cashback ?? "",
          coupon_code: task?.coupon?.coupon,
          user_id: task?.cx_id,
        });
        break;
      case CONTEST_EVENTS.CX_NAME_CLICKED:
        trackEvent(CONTEST_EVENTS.CX_NAME_CLICKED, {
          screen: "Contest task page",
          user_id: task?.cx_id,
        });
        break;
      default:
        break;
    }
  };

  const navigateToCxPage = () => {
    if (task?.cx_id) {
      navigation.navigate("CustomerProfile", {
        user: { user_id: task.cx_id },
        fetchProfile: true,
      });
    }
    handleAnalytics({ type: CONTEST_EVENTS.CX_NAME_CLICKED });
  };

  const onPressCart = () => {
    if (Array.isArray(task?.cart) && task.cart.length !== 0) {
      onPressOrder(task);
      handleAnalytics({ type: CONTEST_EVENTS.CART_VIEWED });
    }
  };

  return task?.is_new_user && !task?.last_ordered_at ? null : (
    <View style={styles.cardContainer}>
      <View style={styles.newUser}>
        {task?.is_new_user && <New isActive={!!task?.is_new_user} />}
      </View>
      <View style={styles.heading}>
        <Pressable onPress={navigateToCxPage}>
          <CustomText
            variant="subtitle_16"
            newfont={true}
            numberOfLines={1}
            style={styles.centerText}
          >
            {task?.user_name}
          </CustomText>
        </Pressable>
        <View style={styles.beLikeRow}>
          <TouchableWithoutFeedback
            onPress={() => {
              handleCallButtonPress(
                task?.user_phone,
                CONTEST_EVENTS.CTA_CLICKED,
                {
                  sub_type: "call",
                  user_id: task.cx_id,
                },
              );
            }}
          >
            <CallIcon style={styles.right} />
          </TouchableWithoutFeedback>
          <TouchableWithoutFeedback
            onPress={() => {
              shareOnWhatsapp({
                message: getTemplate(),
                phoneNumber: task?.user_phone,
                invoke: InvokeType.whatsapp,
              });
              handleAnalytics({
                type: CONTEST_EVENTS.CTA_CLICKED,
                sub_type: "whatsapp",
              });
            }}
          >
            <WhatsappIcon />
          </TouchableWithoutFeedback>
        </View>
      </View>
      {task?.last_ordered_at ? (
        <View style={styles.dateContainer}>
          <CustomText
            variant="body_text_3"
            newfont={true}
            numberOfLines={1}
            style={styles.orderedText}
          >
            {t(`${PATH}/lastOrderedOn`)}
          </CustomText>
          <CustomText
            variant="body_text_4"
            newfont={true}
            numberOfLines={1}
            style={[
              styles.orderedText,
              { marginStart: 4, color: color.mediumBlack },
            ]}
          >
            {getDayMonthYear(task.last_ordered_at)}
          </CustomText>
        </View>
      ) : null}

      {Array.isArray(task?.cart) &&
      task?.cart.length !== 0 &&
      task?.cartTotal ? (
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={onPressCart}
          style={styles.orderCountContainer}
        >
          <View style={styles.saleComiContainer}>
            <CartIcon isActive={true} />
            <View style={styles.commissionContainer}>
              <CustomText
                variant="body_text_3"
                newfont={true}
                numberOfLines={1}
                style={styles.orderedText}
              >
                {`${t(`${PATH}/cartValue`)}: `}
              </CustomText>
              <CustomText
                variant="body_text_4"
                newfont={true}
                numberOfLines={1}
                style={[styles.orderedText, { color: color.mediumBlack }]}
              >
                {`₹${task?.cartTotal ? parseInt(task.cartTotal) : ""}`}
              </CustomText>
            </View>
          </View>
          <View style={styles.itemContainer}>
            <CustomText
              variant="body_text_3"
              newfont={true}
              numberOfLines={1}
              style={[styles.orderedText, { marginEnd: 12 }]}
            >
              {`${Array.isArray(task?.cart) ? task.cart.length : ""} ${t(
                `${PATH}/items`,
              )}`}
            </CustomText>
            <RightArrow fill={color.primary} stroke={color.primary} />
          </View>
        </TouchableOpacity>
      ) : null}

      <View
        style={[
          styles.rowData,
          {
            paddingBottom:
              task?.wallet_cashback || (task?.coupon && task?.coupon.title)
                ? 12
                : 0,
          },
        ]}
      >
        {task?.wallet_cashback && (
          <View style={styles.beLikeColumn}>
            <CustomText
              newfont={true}
              variant="body_text_3"
              style={styles.lightText}
            >
              {t(`${PATH}/walletCashback`)}
            </CustomText>
            <CustomText
              newfont={true}
              variant="subtitle_16"
              style={styles.walletAmount}
            >
              {`₹${task.wallet_cashback}`}
            </CustomText>
          </View>
        )}
        {task?.coupon && task?.coupon.title && (
          <View style={styles.beLikeColumn}>
            <CustomText
              newfont={true}
              variant="body_text_3"
              style={styles.lightText}
            >
              {t(`${PATH}/coupon`)}
            </CustomText>
            <View style={styles.couponWrapper}>
              <Coupon
                width={145}
                height={145}
                color={color.plain_green}
                style={styles.newUser}
              />
              <CustomText
                newfont={true}
                variant="caption_1"
                numberOfLines={1}
                style={styles.walletAmount}
              >
                {`${task.coupon.coupon} | ${task.coupon.title}`}
              </CustomText>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

export default OrderedCard;

const styles = StyleSheet.create({
  cardContainer: {
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
    borderRadius: scale(10),
    backgroundColor: color.white,
    marginBottom: 17,
  },
  newUser: {
    position: "absolute",
    zIndex: 1000,
  },
  heading: {
    paddingHorizontal: 16,
    paddingTop: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  nameTextStyle: {
    width: scale(220),
  },
  centerText: {
    alignItems: "center",
    maxWidth: scale(220),
  },
  beLikeRow: {
    flexDirection: "row",
  },
  right: {
    marginEnd: 20,
  },
  orderCountContainer: {
    backgroundColor: color.veryLightOrange_rgb,
    borderRadius: 10,
    marginTop: 10,
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 14,
    alignItems: "center",
    marginHorizontal: 16,
  },
  saleComiContainer: {
    flexDirection: "row",
    flex: 0.55,
    alignItems: "center",
    justifyContent: "space-between",
  },
  itemContainer: {
    flex: 0.45,
    justifyContent: "flex-end",
    alignItems: "center",
    flexDirection: "row",
  },
  rowData: {
    flexDirection: "row",
    paddingTop: 16,
    paddingHorizontal: 16,
    justifyContent: "space-between",
  },
  beLikeColumn: {
    flexDirection: "column",
  },
  lightText: {
    opacity: 0.56,
  },
  walletAmount: {
    fontFamily: fontFamily.bold,
  },
  couponWrapper: {
    flexDirection: "row",
    paddingHorizontal: 12,
    paddingVertical: 3,
    alignItems: "center",
    marginTop: 2,
  },
  commissionContainer: {
    flexDirection: "row",
  },
  dateContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    alignItems: "center",
    marginTop: 4,
  },
  orderedText: {
    color: color.lightBlack,
  },
});
